import { useState, useEffect } from 'react';
import {
  useTasksByTemplate,
  useCreateTask,
  useUpdateTask,
  useDeleteTask,
  useReorderTasks,
} from '../../../../hooks/useTasks';

/**
 * Hook para gestionar las tareas de una plantilla específica
 */
export const useTasksForTemplate = (templateId, isOpen) => {
  // Estados locales
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [editMode, setEditMode] = useState(false);

  // Queries y mutations
  const { data: tasks = [], isLoading } = useTasksByTemplate(templateId, {
    enabled: !!templateId && isOpen,
  });

  const createTaskMutation = useCreateTask();
  const updateTaskMutation = useUpdateTask();
  const deleteTaskMutation = useDeleteTask();
  const reorderTasksMutation = useReorderTasks();

  // Reset estados cuando se cierra el diálogo
  useEffect(() => {
    if (!isOpen) {
      setTaskDialogOpen(false);
      setSelectedTask(null);
      setEditMode(false);
    }
  }, [isOpen]);

  // Funciones de manejo de diálogos
  const handleCreateTask = () => {
    setSelectedTask(null);
    setEditMode(false);
    setTaskDialogOpen(true);
  };

  const handleEditTask = (task) => {
    setSelectedTask(task);
    setEditMode(true);
    setTaskDialogOpen(true);
  };

  const handleCloseTaskDialog = () => {
    setTaskDialogOpen(false);
    setSelectedTask(null);
    setEditMode(false);
  };

  // Funciones CRUD
  const handleSaveTask = async (taskData) => {
    try {
      const dataWithTemplate = {
        ...taskData,
        uuid_task_template: templateId,
      };

      if (editMode && selectedTask) {
        await updateTaskMutation.mutateAsync({
          id: selectedTask.uuid,
          data: dataWithTemplate,
        });
      } else {
        // Si no se proporciona sequence_order, calcular el siguiente
        if (!dataWithTemplate.sequence_order) {
          const maxOrder = Math.max(...tasks.map((t) => t.sequence_order || 0), 0);
          dataWithTemplate.sequence_order = maxOrder + 1;
        }
        await createTaskMutation.mutateAsync(dataWithTemplate);
      }

      handleCloseTaskDialog();
    } catch (error) {
      console.error('Error saving task:', error);
      throw error;
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (window.confirm('¿Está seguro que desea eliminar esta tarea?')) {
      try {
        await deleteTaskMutation.mutateAsync(taskId);
      } catch (error) {
        console.error('Error deleting task:', error);
      }
    }
  };

  const handleReorderTasks = async (reorderedTasks) => {
    try {
      const taskOrders = reorderedTasks.map((task, index) => ({
        uuid: task.uuid,
        sequence_order: index + 1,
      }));

      await reorderTasksMutation.mutateAsync({
        templateId,
        taskOrders,
      });
    } catch (error) {
      console.error('Error reordering tasks:', error);
    }
  };

  return {
    // Estados de datos
    tasks,
    isLoading,

    // Estados de diálogos
    taskDialogOpen,
    selectedTask,
    editMode,

    // Estados de loading de mutations
    isSaving: createTaskMutation.isPending || updateTaskMutation.isPending,
    isDeleting: deleteTaskMutation.isPending,
    isReordering: reorderTasksMutation.isPending,

    // Funciones de manejo
    handleCreateTask,
    handleEditTask,
    handleDeleteTask,
    handleReorderTasks,
    handleCloseTaskDialog,
    handleSaveTask,
  };
};
